import React, { useEffect, useState } from "react";
import { login, loginGoogle, register } from "../../function/auth";
import { Spin } from "antd";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { FcGoogle } from "react-icons/fc";
import { SiLine } from "react-icons/si";
import { auth, googleAuthProvider } from "../../firebase";
import axios from "axios";
import logo from "./logo/logo.png";

const formVariants = {
  enterFromRight: { x: 60, opacity: 0 },
  enterFromLeft: { x: -60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exitToLeft: { x: -60, opacity: 0 },
  exitToRight: { x: 60, opacity: 0 },
};

// variants สำหรับ logo panel
const logoVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
};

const AuthPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [loading, setLoading] = useState(false);

  const [loginValue, setLoginValue] = useState({
    usernameOrEmail: "",
    password: "",
  });
  const [regValue, setRegValue] = useState({
    username: "",
    email: "",
    password: "",
    password1: "",
  });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  useEffect(() => {
    const handleFocusOut = () => window.scrollTo(0, 0);
    window.addEventListener("focusout", handleFocusOut);
    return () => window.removeEventListener("focusout", handleFocusOut);
  }, []);

  const roleBaseRedirect = (role) => {
    const intended = location.state;
    if (intended) {
      navigate("../" + intended);
      return;
    }
    if (role === "admin") navigate("/admin/index");
    else if (role === "employee") navigate("/employ-shop");
    else if (role === "staff") navigate("/admin/branch");
    else if (role === "stock") navigate("/stock/orders");
    else navigate("/");
  };

  // ── Login ────────────────────────────────────────────────────
  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    login({
      usernameOrEmail: loginValue.usernameOrEmail,
      password: loginValue.password,
    })
      .then((res) => {
        setLoading(false);
        const user = res.data.payload.user;
        toast.success(`${user.username} Login Success`);
        dispatch({
          type: "LOGIN",
          payload: {
            token: res.data.token,
            username: user.username,
            role: user.role,
            profileImage: user.profileImage?.url,
          },
        });
        localStorage.setItem("token", res.data.token);
        roleBaseRedirect(user.role);
        setTimeout(() => window.location.reload(), 500);
      })
      .catch(() => {
        setLoading(false);
        toast.error("ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง");
      });
  };

  // ── Register ─────────────────────────────────────────────────
  const isValidUsername = (text) => /^[a-zA-Z0-9_.]+$/.test(text);

  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegValue((prev) => ({ ...prev, [name]: value.replace(/\s/g, "") }));
  };

  const handleRegister = (e) => {
    e.preventDefault();
    if (!isValidUsername(regValue.username)) {
      toast.error("Username ใช้ได้เฉพาะ a-z, A-Z, 0-9 และ _ เท่านั้น");
      return;
    }
    if (regValue.password !== regValue.password1) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }
    register(regValue)
      .then(() => {
        toast.success("สมัครสมาชิกสำเร็จ!");
        setMode("login");
      })
      .catch((err) =>
        toast.error(err.response?.data || "สมัครสมาชิกไม่สำเร็จ"),
      );
  };

  // ── Social ───────────────────────────────────────────────────
  const handleLoginByGoogle = async () => {
    try {
      const result = await auth.signInWithPopup(googleAuthProvider);
      const { user } = result;
      const idTokenResult = await user.getIdTokenResult();
      const { data } = await loginGoogle({
        idToken: idTokenResult.token,
        username: user.displayName,
        email: user.email,
        picture: user.photoURL,
      });
      if (data?.user?.username) {
        dispatch({
          type: "LOGIN",
          payload: {
            token: data.token,
            username: data.user.username,
            email: data.user.email,
            role: data.user.role,
          },
        });
        localStorage.setItem("token", data.token);
        roleBaseRedirect(data.user.role);
      }
    } catch {
      toast.error("Google login failed");
    }
  };

  const handleLoginByLine = () => {
    const lineUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${process.env.REACT_APP_LINE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_LINE_REDIRECT_URI}&state=randomState123&scope=profile openid email`;
    window.location.href = lineUrl;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/forgot-password`, {
        email: forgotEmail,
      });
      toast.success("ส่งลิงก์รีเซ็ตรหัสผ่านไปที่อีเมลแล้ว");
      setShowForgot(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data || "ไม่พบอีเมลนี้ในระบบ");
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Shared styles ────────────────────────────────────────────
  const logoPanel = (gradient, subText) => (
    <div
      style={{
        flex: "0 0 38%",
        background: gradient,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        gap: "1rem",
      }}
      className="d-none d-md-flex"
    >
      <div
        style={{
          width: 110,
          height: 110,
          background: "white",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}
      >
        <img
          src={logo}
          alt="Logo"
          style={{ width: 82, objectFit: "contain" }}
        />
      </div>
      <p
        style={{
          color: "white",
          fontWeight: 500,
          margin: 0,
          fontSize: 15,
          textAlign: "center",
        }}
      >
        360 Healthy Shop
      </p>
      <p
        style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: 13,
          margin: 0,
          textAlign: "center",
        }}
      >
        {subText}
      </p>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        background: "#f5f5f5",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 860,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 3px 20px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          minHeight: 500,
        }}
      >
        <AnimatePresence mode="wait">
          {mode === "login" ? (
            // ── LOGIN ──────────────────────────────────────────
            <motion.div
              key="login"
              style={{ display: "flex", width: "100%" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {/* Logo ซ้าย */}
              <motion.div
                initial={{ x: -40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{
                  flex: "0 0 38%",
                  background:
                    "linear-gradient(160deg,#FF70A6 0%,#c24ef0 50%,#4CB4F6 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2rem",
                  gap: "1rem",
                }}
                className="d-none d-md-flex"
              >
                <div
                  style={{
                    width: 110,
                    height: 110,
                    background: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={logo}
                    alt="Logo"
                    style={{ width: 82, objectFit: "contain" }}
                  />
                </div>
                <p
                  style={{
                    color: "white",
                    fontWeight: 500,
                    margin: 0,
                    fontSize: 15,
                    textAlign: "center",
                  }}
                >
                  360 Healthy Shop
                </p>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: 13,
                    margin: 0,
                    textAlign: "center",
                  }}
                >
                  ดูแลสุขภาพของคุณ
                </p>
              </motion.div>

              {/* Form ขวา */}
              <motion.div
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{
                  flex: 1,
                  padding: "2.5rem 2rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 12,
                }}
              >
                <div className="text-center mb-3 d-md-none">
                  <img src={logo} alt="Logo" style={{ maxWidth: 100 }} />
                </div>

                {loading ? (
                  <div className="text-center">
                    <Spin />
                  </div>
                ) : (
                  <div>
                    <h4 style={{ fontWeight: 500, marginBottom: 2 }}>
                      เข้าสู่ระบบ
                    </h4>
                    <p style={{ color: "#888", fontSize: 13, marginBottom: 0 }}>
                      ยินดีต้อนรับกลับมา
                    </p>
                  </div>
                )}

                <form
                  onSubmit={handleLogin}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Username or Email
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      value={loginValue.usernameOrEmail}
                      onChange={(e) =>
                        setLoginValue({
                          ...loginValue,
                          usernameOrEmail: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Password
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      value={loginValue.password}
                      onChange={(e) =>
                        setLoginValue({
                          ...loginValue,
                          password: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div style={{ textAlign: "right", marginTop: -4 }}>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#4CB4F6",
                      fontSize: 12,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    ลืมรหัสผ่าน?
                  </button>
                </div>
                  
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn flex-fill"
                      style={{ background: "#FF70A6", color: "#fff" }}
                    >
                      เข้าสู่ระบบ
                    </button>
                    <button
                      type="button"
                      className="btn flex-fill"
                      style={{ background: "#4CB4F6", color: "#fff" }}
                      onClick={() => setMode("register")}
                    >
                      สมัครสมาชิก
                    </button>
                  </div>
                </form>

                

                <hr style={{ margin: "4px 0", borderColor: "#eee" }} />

                <div className="d-flex gap-2">
                  <Button
                    onClick={handleLoginByGoogle}
                    variant="outlined"
                    startIcon={<FcGoogle size={16} />}
                    style={{
                      flex: 1,
                      textTransform: "none",
                      fontSize: 13,
                      borderColor: "#ddd",
                      color: "#555",
                    }}
                  >
                    Google
                  </Button>
                  <Button
                    onClick={handleLoginByLine}
                    variant="contained"
                    startIcon={<SiLine size={14} />}
                    style={{
                      flex: 1,
                      textTransform: "none",
                      fontSize: 13,
                      background: "#06C755",
                      color: "#fff",
                    }}
                  >
                    LINE
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            // ── REGISTER ────────────────────────────────────────
            <motion.div
              key="register"
              style={{ display: "flex", width: "100%" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {/* Form ซ้าย */}
              <motion.div
                initial={{ x: -40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{
                  flex: 1,
                  padding: "2.5rem 2rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                <div className="text-center mb-3 d-md-none">
                  <img src={logo} alt="Logo" style={{ maxWidth: 100 }} />
                </div>

                <div>
                  <h4 style={{ fontWeight: 500, marginBottom: 2 }}>
                    สมัครสมาชิก
                  </h4>
                  <p style={{ color: "#888", fontSize: 13, marginBottom: 0 }}>
                    สร้างบัญชีใหม่
                  </p>
                </div>

                <form
                  onSubmit={handleRegister}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Username
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      name="username"
                      onChange={handleRegChange}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Email
                    </label>
                    <input
                      className="form-control"
                      type="email"
                      name="email"
                      onChange={handleRegChange}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Password
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      name="password"
                      autoComplete="new-password"
                      onChange={handleRegChange}
                      onKeyDown={(e) => {
                        if (e.key === " ") {
                          e.preventDefault();
                          toast.warning("ไม่สามารถใช้ช่องว่างได้");
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Confirm Password
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      name="password1"
                      onChange={handleRegChange}
                      onKeyDown={(e) => {
                        if (e.key === " ") {
                          e.preventDefault();
                          toast.warning("ไม่สามารถใช้ช่องว่างได้");
                        }
                      }}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn flex-fill"
                      style={{ background: "#4CB4F6", color: "#fff" }}
                      disabled={regValue.password.length < 6}
                    >
                      ยืนยันสมัคร
                    </button>
                    <button
                      type="button"
                      className="btn flex-fill"
                      style={{ background: "#eee", color: "#555" }}
                      onClick={() => setMode("login")}
                    >
                      กลับไปเข้าสู่ระบบ
                    </button>
                  </div>
                </form>
              </motion.div>

              {/* Logo ขวา */}
              <motion.div
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{
                  flex: "0 0 38%",
                  background:
                    "linear-gradient(160deg,#4CB4F6 0%,#36c8b8 50%,#4df5a0 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2rem",
                  gap: "1rem",
                }}
                className="d-none d-md-flex"
              >
                <div
                  style={{
                    width: 110,
                    height: 110,
                    background: "white",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={logo}
                    alt="Logo"
                    style={{ width: 82, objectFit: "contain" }}
                  />
                </div>
                <p
                  style={{
                    color: "white",
                    fontWeight: 500,
                    margin: 0,
                    fontSize: 15,
                    textAlign: "center",
                  }}
                >
                  360 Healthy Shop
                </p>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: 13,
                    margin: 0,
                    textAlign: "center",
                  }}
                >
                  เริ่มต้นดูแลสุขภาพกับเรา
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showForgot && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowForgot(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.4)",
                  zIndex: 999,
                }}
              />
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  background: "#fff",
                  borderRadius: 12,
                  padding: "2rem",
                  width: "90%",
                  maxWidth: 380,
                  zIndex: 1000,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                }}
              >
                <h5 style={{ fontWeight: 500, marginBottom: 6 }}>
                  ลืมรหัสผ่าน
                </h5>
                <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
                  กรอกอีเมลที่ลงทะเบียนไว้ ระบบจะส่งลิงก์รีเซ็ตให้
                </p>

                <form
                  onSubmit={handleForgotPassword}
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: 13,
                        color: "#666",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Email
                    </label>
                    <input
                      className="form-control"
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="example@email.com"
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn flex-fill"
                      style={{ background: "#FF70A6", color: "#fff" }}
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? <Spin size="small" /> : "ส่งลิงก์รีเซ็ต"}
                    </button>
                    <button
                      type="button"
                      className="btn flex-fill"
                      style={{ background: "#eee", color: "#555" }}
                      onClick={() => setShowForgot(false)}
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AuthPage;
